import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { Bot } from '@/icons/Bot';
import { Hourglass } from '@/icons/Hourglass';
import { Sparkle } from '@/icons/Sparkle';
import { Video } from '@/icons/Video';
import { cn } from '@/utils/cn';
import {
  endConnection,
  finishTransaction,
  getAvailablePurchases,
  getSubscriptions,
  initConnection,
  purchaseUpdatedListener,
  requestPurchase
} from '@/utils/iap';
import { products } from '@/utils/products';
import Head from 'expo-router/head';
import * as WebBrowser from 'expo-web-browser';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Linking,
  Platform,
  Pressable,
  View,
  StyleSheet
} from 'react-native';
import { useAuth } from '@/contexts/AuthContext';
import { generateAPIUrl } from '@/utils/api-url';
import type { LucideIcon } from 'lucide-react-native';
import {
  SafeAreaView,
  useSafeAreaInsets
} from 'react-native-safe-area-context';
import type { ApiCheckoutType } from './api/checkout/[...everything]+api';
import BottomSheet, { BottomSheetView } from '@gorhom/bottom-sheet';

const PRODUCT_SKU = products[0].id;

type Product = {
  id: string;
  displayPrice?: string;
  subscriptionOfferDetails?: { offerToken: string }[];
  offerToken?: string; // android only
};

const checkoutClient = hc<ApiCheckoutType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

async function createCheckoutUrl(token: string, id: string) {
  const res = await checkoutClient.api.checkout[':id'].$post(
    {
      param: {
        id
      }
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string'
        ? data.error
        : 'Failed to create checkout URL'
    );
  }

  return data;
}

async function postIAPReceipt(
  token: string,
  iapSource: 'ios' | 'android',
  receipt: string
) {
  const res = await checkoutClient.api.checkout.iap.$post(
    {
      json: {
        iapSource,
        transactionReceipt: receipt
      }
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = await res.json();
  if (data.valid !== true) {
    if (res.status === 410) {
      // sometimes, at least during development,
      // the `purchaseUpdatedListener` will be called with old, expired receipts
      // upon page load
      // alert('Posted subscription is expired');
      return null;
    }

    throw new Error('Failed to verify IAP receipt');
  }

  return data;
}

export default function Upgrade() {
  const insets = useSafeAreaInsets();
  const { authState, user, updateAuthState } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const updateUser = (attrs: { planKeyname: string; planInterval: string }) => {
    updateAuthState?.({
      authState,
      user: user ? { ...user, ...attrs } : undefined
    });
  };

  useEffect(() => {
    const setupIAP = async () => {
      if (await initConnection()) {
        setIsConnected(true);

        const products = await getSubscriptions([PRODUCT_SKU]);
        if (products.length) {
          // console.log('Products:', products[0]);
          setProduct(products[0]);
        } else {
          alert('No products found');
        }
      } else {
        alert(
          `Unable to establish a connection to the ${
            Platform.OS === 'ios' ? 'App Store' : 'Google Play'
          } store.`
        );
      }
    };
    setupIAP();

    const purchaseListener =
      Platform.OS === 'web'
        ? null
        : purchaseUpdatedListener(async (purchase) => {
            if (!authState.token) {
              alert('Must be logged in to purchase??');
              return;
            }

            // console.log('PURCHASE!', purchase);
            if (purchase) {
              await finishTransaction({ purchase });
              setIsLoading(true);
              const iapResult = await postIAPReceipt(
                authState.token,
                Platform.OS === 'ios' ? 'ios' : 'android',
                purchase.transactionReceipt
              );
              if (iapResult?.subscription) {
                updateUser(iapResult.subscription);
              }
            }

            setIsLoading(false);
          });

    return () => {
      purchaseListener?.remove();
      endConnection();
    };
  }, [authState.token]);

  const productId = product?.id;
  const firstOfferToken = product?.subscriptionOfferDetails?.[0]?.offerToken;

  const redirectToCheckout = useCallback(async () => {
    if (!authState.token) {
      alert('Must be logged in to purcahse');
    }

    if (!productId) {
      alert('No product');
    }

    try {
      setIsLoading(true);
      const data = await createCheckoutUrl(authState.token!, productId!);

      // @ts-ignore - this works in react-native-web: https://necolas.github.io/react-native-web/docs/linking/#static-methods
      Linking.openURL(data.url, '_self');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'An unknown error ocurred');
    } finally {
      setIsLoading(false);
    }
  }, [authState.token, productId]);

  const buyItem = useCallback(async () => {
    if (!productId) {
      alert('No product available');
      return;
    }
    if (!firstOfferToken && Platform.OS === 'android') {
      alert('No offer token available');
      return;
    }
    if (!authState.token) {
      alert('Must be logged in to purchase');
      return;
    }

    // console.log('Buying item with ID:', productId);
    if (Platform.OS === 'web') {
      await redirectToCheckout();
      return;
    }

    setIsLoading(true);

    try {
      await requestPurchase({
        request:
          Platform.OS === 'android'
            ? {
                skus: [productId],
                subscriptionOffers: [
                  { sku: productId, offerToken: firstOfferToken! }
                ]
              }
            : { sku: productId },
        type: 'subs'
      });
    } catch (error) {
      if (error instanceof Error) {
        if (
          // iOS
          error.message === 'Purchase failed: User cancelled the purchase' ||
          // Android
          error.message === 'Payment is cancelled.'
        ) {
          return;
        }
        console.error(error);
        alert(error.message);
      } else {
        console.error(error);
        alert('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  }, [productId, authState.token, firstOfferToken, redirectToCheckout]);

  // iOS / android only
  const restorePurchases = useCallback(async () => {
    if (!authState.token) {
      alert('Please log in to restore purchases');
      return;
    }
    try {
      const result = await getAvailablePurchases({
        alsoPublishToEventListener: false, // `finishTransaction` doesn't seem to work here
        onlyIncludeActiveItems: true
      });
      if (result.length === 0) {
        alert('No active purchases found');
        return;
      }

      setIsLoading(true);

      for (const purchase of result) {
        const iapResult = await postIAPReceipt(
          authState.token,
          Platform.OS === 'ios' ? 'ios' : 'android',
          purchase.transactionReceipt
        );
        if (iapResult?.subscription) {
          updateUser(iapResult.subscription);
        }
      }

      setIsLoading(false);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Purchase failed: User cancelled the purchase') {
          return;
        }
        alert(error.message);
      } else {
        console.error(error);
        alert('An unexpected error occurred');
      }
    }
  }, [authState.token]);

  const openURL = useCallback((url: string) => {
    if (Platform.OS === 'web') {
      Linking.openURL(url);
    } else {
      WebBrowser.openBrowserAsync(url);
    }
  }, []);

  const isConnectedAndNotLoading = isConnected && !isLoading;
  const hasSubscription = user?.planKeyname !== 'free';

  const features = [
    {
      id: '1',
      Icon: Video,
      iconClassName: 'bg-persimmon-500',
      title: 'Process up to 5 minutes of conversation',
      description:
        'Upload longer videos and process extended conversations for deeper context.'
    },
    {
      id: '2',
      Icon: Hourglass,
      iconClassName: 'bg-lagoon-500',
      title: 'No waiting time for responses',
      description:
        'Enjoy instant replies with significantly reduced rate limits and restrictions.'
    },
    {
      id: '3',
      Icon: Bot,
      iconClassName: 'bg-periwinkle-500',
      title: 'Switch between different AI models',
      description:
        'Access multiple AI models for varied conversation styles and capabilities.'
    },
    {
      id: '4',
      Icon: Sparkle,
      iconClassName: 'bg-chartreuse-500',
      title: 'Early access to new features',
      description:
        "Be the first to experience exciting new capabilities as they're developed."
    }
  ];

  return (
    <Fragment>
      <Head>
        <title>Upgrade - Lexikon AI</title>
      </Head>
      <SafeAreaView edges={['left', 'right', 'bottom']} className="flex-1">
        <View className="flex-1 pt-2 web:self-center web:lg:w-[50%]">
          <FlatList
            data={features}
            renderItem={({ item }) => (
              <Feature
                Icon={item.Icon}
                iconClassName={item.iconClassName}
                title={item.title}
                description={item.description}
              />
            )}
            ListFooterComponent={<View className="min-h-[240px]"></View>}
            ListHeaderComponent={
              <Text className="my-12 px-4 text-center text-2xl font-semibold">
                Unlock full access
              </Text>
            }
            keyExtractor={(item) => item.id}
          />

          <BottomSheet
            snapPoints={[200]}
            enableDynamicSizing={false}
            handleComponent={() => null}
            topInset={insets.top}
            bottomInset={insets.bottom}
            backgroundStyle={styles.bottomSheetBackgroundStyle}
          >
            <BottomSheetView style={styles.bottomSheetView}>
              <View className="px-8 py-4">
                {isConnectedAndNotLoading ? (
                  hasSubscription ? (
                    <View className="mt-4">
                      <Text className="text-center leading-7">
                        You are currently on the{' '}
                        {user?.planKeyname === 'premium'
                          ? 'Pro'
                          : user?.planKeyname}{' '}
                        plan. Thanks for subscribing! 🎉
                      </Text>
                    </View>
                  ) : (
                    <Fragment>
                      {product?.displayPrice ? (
                        <Text className="text-center text-lg font-bold web:font-medium">
                          Only {product.displayPrice}/month
                        </Text>
                      ) : null}

                      <Button
                        size="lg"
                        className="mt-4 w-full rounded-full web:self-center web:lg:w-[50%]"
                        onPress={buyItem}
                      >
                        <Text>Upgrade Now</Text>
                      </Button>

                      {Platform.OS !== 'web' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-4 w-full rounded-full web:self-center web:lg:w-[50%]"
                          onPress={restorePurchases}
                        >
                          <Text>Restore Purchases</Text>
                        </Button>
                      )}
                    </Fragment>
                  )
                ) : (
                  <View className="my-4 self-center">
                    <ActivityIndicator color="white" size="small" />
                  </View>
                )}
                <View className="w-full web:self-center web:lg:w-[50%]">
                  <View className="mt-4 flex flex-row justify-between">
                    <Pressable
                      className="pb-0 pt-0"
                      onPress={() => {
                        openURL('https://lexikon.ai/terms');
                      }}
                    >
                      <Text className="text-sm text-primary underline">
                        Terms and Conditions
                      </Text>
                    </Pressable>
                    <Pressable
                      className="pb-0 pt-0"
                      onPress={() => {
                        openURL('https://lexikon.ai/privacy');
                      }}
                    >
                      <Text className="text-sm text-primary underline">
                        Privacy Policy
                      </Text>
                    </Pressable>
                  </View>
                </View>
              </View>
            </BottomSheetView>
          </BottomSheet>
        </View>
      </SafeAreaView>
    </Fragment>
  );
}

function Feature({
  title,
  description,
  iconClassName,
  Icon
}: {
  title: string;
  description: string;
  Icon: LucideIcon;
  iconClassName?: string;
}) {
  return (
    <View className="mb-5 ml-5 mr-8 flex flex-row">
      <View className={cn('h-12 w-12 rounded-full p-3', iconClassName)}>
        <Icon size={24} />
      </View>
      <View>
        <Text className="px-4 text-xl font-semibold">{title}</Text>
        <Text className="px-4 leading-7">{description}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bottomSheetBackgroundStyle: {
    backgroundColor: '#000'
  },
  bottomSheetView: {
    flex: 1
  }
});
